<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { PropType } from 'vue';
import type { PublicCaseStudyListItem } from '../types';

defineOptions({
  name: 'CaseCard',
});

const { t } = useI18n();
const router = useRouter();
const props = defineProps({
  item: {
    type: Object as PropType<PublicCaseStudyListItem>,
    required: true,
  },
});

const handleClick = () => {
  if (props.item.detailType === 0) {
    return;
  }

  if (props.item.detailType === 1) {
    router.push(`/case-study/${props.item.id}`);
    return;
  }

  if (props.item.externalUrl) {
    window.open(props.item.externalUrl, '_blank');
  }
};
</script>

<template>
  <li
    class="case-card group relative overflow-hidden rounded-4 bg-white shadow-[0_0_16px_0_rgba(0,0,0,0.08)] transition-all duration-300 hover:translate-y--4px hover:shadow-[0_8px_24px_0_rgba(0,0,0,0.12)]"
    :class="{ 'cursor-pointer': item.detailType !== 0 }"
    @click="handleClick"
  >
    <div class="relative h-180 overflow-hidden <md:h-160">
      <img
        :src="item.imageUrl"
        :alt="item.name"
        class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      >
      <span class="pointer-events-none absolute bottom-12 right-12 rounded-2 bg-[rgba(0,0,0,0.48)] px-8 py-4 text-12 text-white leading-16">
        {{ t('home.caseStudy.aiGenerated') }}
      </span>
    </div>

    <div class="p-24 <md:p-16">

      <div class="flex flex-wrap gap-8">
        <span
          v-for="industry in item.industryList"
          :key="industry.id"
          class="rounded-2 bg-[rgba(17,128,255,0.08)] px-8 py-4 text-12 color-brand-primary"
        >
          {{ industry.name }}
        </span>
        <span
          class="rounded-2 bg-[rgba(45,45,45,0.04)] px-8 py-4 text-12 color-#2d2d2d"
        >
          {{ item.project.name }}
        </span>
      </div>

      <h3 class="line-clamp-2 m-0 mt-8 text-18 color-#2d2d2d font-500 leading-26">{{ item.name }}</h3>

      <div class="mt-4 h-34">
        <p class="m-0 truncate text-14 color-#9A9EA6" v-if="item.partyA">{{ t('home.caseStudy.partyA') }}{{ item.partyA }}</p>
        <p class="m-0 truncate text-14 color-#9A9EA6" v-if="item.partner">{{ t('home.caseStudy.partner') }}{{ item.partner }}</p>
      </div>

      <p class="m-0 mt-4 truncate text-14 color-#9A9EA6">{{ t('home.caseStudy.contact') }}{{ item.project.email ?? '' }}</p>

      <div v-if="item.detailType" class="mt-16 flex items-center text-14 color-brand-primary">
        <span>{{ t('home.caseStudy.viewDetail') }}</span>
        <span class="i-home-icons:arrow-right ml-4 size-16 transition-transform group-hover:translate-x-4px"></span>
      </div>
    </div>
  </li>
</template>

<style lang="scss" scoped>
.case-card {
  border: 1px solid rgba(154, 158, 166, 0.1);
}
</style>
